import React, { useId } from "react";
import { EXPRESS_INVOICE_JOURNAL_ENTRY_MODE_MESSAGE } from "@common/constants/stripeInvoiceJournalEntryMode";
import { InvoiceJournalEntryMode } from "@common/types/integrationAccount";
import { useUpdateInvoiceJournalEntryModeMutation } from "@api/stripe.api";
import { Picker } from "@design-system/Picker/Picker.lib";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { VStack } from "@design-system/VStack/VStack";
import { isErrorToastedByBackendClient } from "@lib/backendClient";
import { AccountDetailsField } from "./AccountDetailsField";
import styles from "./StripeInvoiceJournalEntryModeField.module.scss";

export const JOURNAL_ENTRY_MODE_LABELS: Record<
    InvoiceJournalEntryMode,
    string
> = {
    [InvoiceJournalEntryMode.ALWAYS]: "Always create journal entries",
    [InvoiceJournalEntryMode.INFER]:
        "Skip for invoices paid by card right away",
    [InvoiceJournalEntryMode.NEVER]: "Never create journal entries",
};

const MODE_OPTIONS = Object.values(InvoiceJournalEntryMode);

interface Props {
    workspaceId: string;
    integrationAccountId: string;
    stripeAccountId: string | null;
    mode: InvoiceJournalEntryMode;
    isExpress: boolean;
}

export const StripeInvoiceJournalEntryModeField: React.FC<Props> = ({
    workspaceId,
    integrationAccountId,
    stripeAccountId,
    mode,
    isExpress,
}) => {
    const toaster = useToaster();
    const titleId = useId();
    const updateMode = useUpdateInvoiceJournalEntryModeMutation({
        previousMode: mode,
    });
    const shownMode = isExpress ? InvoiceJournalEntryMode.ALWAYS : mode;

    const handleValueChange = (next: InvoiceJournalEntryMode | null) => {
        if (next === null) {
            return;
        }
        updateMode.mutate(
            { workspaceId, integrationAccountId, mode: next },
            {
                onError: (error) => {
                    if (!isErrorToastedByBackendClient(error)) {
                        toaster.error("Failed to update journal entries");
                    }
                },
            },
        );
    };

    if (isExpress) {
        return null;
    }

    return (
        <AccountDetailsField
            title={
                <span id={titleId}>
                    Journal entries for new invoices
                    {stripeAccountId ? ` (${stripeAccountId})` : null}
                </span>
            }
        >
            <VStack gap={8} align="end">
                <Picker.Select<InvoiceJournalEntryMode>
                    value={shownMode}
                    onValueChange={handleValueChange}
                    disabled={isExpress || updateMode.isPending}
                >
                    <Picker.ButtonTrigger
                        render={
                            <Picker.Button
                                variant="primary"
                                size="md"
                                aria-labelledby={titleId}
                            />
                        }
                        value={JOURNAL_ENTRY_MODE_LABELS[shownMode]}
                        tooltip={
                            isExpress
                                ? EXPRESS_INVOICE_JOURNAL_ENTRY_MODE_MESSAGE
                                : undefined
                        }
                    />
                    <Picker.Popup>
                        <Picker.List>
                            {MODE_OPTIONS.map((option) => (
                                <Picker.Item key={option} value={option}>
                                    {JOURNAL_ENTRY_MODE_LABELS[option]}
                                </Picker.Item>
                            ))}
                        </Picker.List>
                    </Picker.Popup>
                </Picker.Select>
                <small className={styles.helper}>
                    Applies to newly imported invoices only. Existing invoices
                    keep their journal entries.
                </small>
            </VStack>
        </AccountDetailsField>
    );
};
